// Modal for raising an incident on a specific service. Mounted from the
// service detail page's primary action.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { triggerIncident } from '../../lib/incidents';
import type { IncidentPriority } from '../../lib/types';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  FormField,
  Input,
  Stack,
} from '../ui';
import { ErrorBanner } from '../../pages/SigninPage';

export function TriggerIncidentDialog({
  orgId,
  serviceId,
  serviceName,
  defaultPriority,
  open,
  onClose,
}: {
  orgId: string;
  serviceId: string;
  serviceName: string;
  defaultPriority: IncidentPriority;
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<IncidentPriority>(defaultPriority);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on open so a previous attempt doesn't leak state.
  useEffect(() => {
    if (open) {
      setTitle('');
      setDescription('');
      setPriority(defaultPriority);
      setError(null);
    }
  }, [open, defaultPriority]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const incidentId = await triggerIncident(orgId, {
        serviceId,
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
      });
      navigate(`/incidents/${incidentId}`);
    } catch (err) {
      console.error('[trigger-incident] failed', err);
      setError(extractMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? () => undefined : onClose}
      ariaLabel="Trigger incident"
      dismissOnBackdrop={!busy}
    >
      <DialogHeader>Trigger an incident</DialogHeader>
      <DialogBody>
        <form id="trigger-incident-form" onSubmit={submit}>
          <Stack gap={4}>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              On <strong>{serviceName}</strong>. Anyone in the org will see this in the
              incidents list.
            </p>

            {error && <ErrorBanner message={error} />}

            <FormField label="Title">
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  maxLength={140}
                  autoFocus
                  placeholder="API gateway returning 5xx"
                />
              )}
            </FormField>

            <FormField label="Description" hint="Optional. Useful context for whoever responds.">
              {(id) => (
                <textarea
                  id={id}
                  className="ds-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="What you've seen, what you've already tried."
                />
              )}
            </FormField>

            <FormField label="Priority" hint={`Defaults to ${defaultPriority} from service tier.`}>
              {(id) => (
                <select
                  id={id}
                  className="ds-input"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as IncidentPriority)}
                >
                  <option value="P1">P1 — Critical</option>
                  <option value="P2">P2 — Major</option>
                  <option value="P3">P3 — Minor</option>
                </select>
              )}
            </FormField>
          </Stack>
        </form>
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          form="trigger-incident-form"
          type="submit"
          variant="danger"
          loading={busy}
          disabled={!title.trim()}
        >
          Trigger incident
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Something went wrong. Try again.';
}
